/**
 * Single source of truth for the event lifecycle phases.
 *
 * Both the event-config arrow bar and the dashboard header (colored dot +
 * label badge + tooltip) read from this list so the colors and wording always
 * match. `color` is a hex string so it can be applied via inline
 * `background-color` on dots/arrows. `id` is the value persisted in
 * event_settings under ckey='phase'.
 */
export interface EventPhase {
  id: string;
  label: string;
  color: string;
  tip: string;
}

export const EVENT_PHASES: EventPhase[] = [
  { id: 'teaser', label: 'Teaser', color: '#d97706', tip: 'Pre-launch teaser page is live; registration not open yet.' },
  { id: 'test', label: 'Test run', color: '#64748b', tip: 'Internal test mode for previewing the event setup.' },
  { id: 'pre-event', label: 'Pre-event', color: '#2563eb', tip: 'Registration and booking open in the run-up to the event.' },
  { id: 'live', label: 'Event LIVE', color: '#16a34a', tip: 'Event is running; new bookings are stopped.' },
  { id: 'post-event', label: 'Post-event', color: '#7c3aed', tip: 'Event finished; gallery and recap content are shown.' },
];

/** Default when nothing is stored yet — keeps the header dot green. */
export const DEFAULT_PHASE_ID = 'live';

/**
 * Runtime deployment flags that the SPA routing actually reads (from
 * assets/config.json via RuntimeConfigService / EventModeGuard). The lifecycle
 * phase is the single control: saving a phase derives these and writes them into
 * config.json. `client` and `showGalleryAlways` are NOT derived (client is the
 * deployment selector; showGalleryAlways is a manual post-event sub-choice).
 */
export interface PhaseRuntimeFlags {
  show_teaser: boolean;
  event_finish: boolean;
  show_gallery: boolean;
}

/** Map a phase id to the runtime flags it implies. */
export function phaseRuntimeFlags(phaseId: string): PhaseRuntimeFlags {
  switch (phaseId) {
    case 'teaser':
      // Pre-launch teaser page is live; nothing else public.
      return { show_teaser: true, event_finish: false, show_gallery: false };
    case 'post-event':
      // Event finished; gallery/recap shown (login-gated unless showGalleryAlways).
      return { show_teaser: false, event_finish: true, show_gallery: true };
    case 'test':
    case 'pre-event':
    case 'live':
    default:
      // Normal app: login + registration/booking flow, no teaser, no gallery.
      return { show_teaser: false, event_finish: false, show_gallery: false };
  }
}
